/**
 * I passi del tutorial guidato della pagina "Associa prodotti".
 *
 * La pagina e' la stessa per Amazon ed eBay - cambia solo `platform`, preso dalla
 * route - e i passi lo sono di conseguenza: il nome del marketplace arriva da
 * fuori, tutto il resto e' identico.
 *
 * Qui non c'e' niente da preparare - nessun menu da aprire - quindi nessun passo
 * ha un `before`. I due bersagli che dipendono dai dati hanno `timeout: 250`: a
 * elenco vuoto la card si aggancia in basso invece di far aspettare.
 *
 * @param {{ platformLabel: string }} options 'Amazon' oppure 'eBay'.
 */
export function createUnmatchedTourSteps({ platformLabel }) {
	return [
		{
			body: `Qui trovi le inserzioni che stanno su ${platformLabel} ma che non risultano collegate a nessun prodotto del tuo catalogo: tipicamente roba pubblicata prima di usare Azseller, o da un altro strumento. Finche' restano qui non le gestisci da Azseller - non ne segui giacenza ne' prezzo - e il lavoro di questa pagina e' proprio agganciarle, una per volta.`,
			key: 'intro',
			placement: 'bottom',
			target: '[data-tour="tutorial"]',
			title: 'Le pubblicazioni senza prodotto',
		},
		{
			body: `Ogni paese ha il suo elenco e il suo aggiornamento, quindi la prima cosa e' scegliere di quale ti stai occupando. Nella tendina compaiono solo i paesi dove hai davvero pubblicazioni, col numero accanto, e si parte gia' su quello che usi di piu'. Cambiando paese l'elenco riparte da zero: quello che vedi vale sempre e solo per il paese selezionato.`,
			key: 'country',
			placement: 'bottom',
			target: '[data-tour="country"]',
			title: 'Scegliere il paese',
		},
		{
			body: `E' il pulsante che fa il lavoro vero: chiede a ${platformLabel} il report delle inserzioni attive e lo confronta con il tuo catalogo. Non e' istantaneo - e' un lavoro in coda che puo' durare minuti - quindi non serve restare a guardare: arriva una notifica quando e' pronto, e intanto la pagina si ricontrolla da sola ogni quindici secondi. Se un aggiornamento per quel paese e' gia' in corso te lo dice invece di lanciarne un secondo.`,
			key: 'scan',
			placement: 'bottom',
			target: '[data-tour="scan"]',
			title: 'Aggiorna elenco',
		},
		{
			body: `Ricarica non fa ripartire niente: rilegge soltanto lo stato e l'elenco cosi' come sono adesso. E' quello che serve quando la notifica e' arrivata e vuoi vedere subito il risultato, o quando torni sulla pagina dopo un po'. Da non confondere con Aggiorna elenco, che invece rimette in moto la lettura del report.`,
			key: 'refresh',
			placement: 'bottom',
			target: '[data-tour="refresh"]',
			title: 'Ricarica',
		},
		{
			body: `Questa fascia dice sempre a che punto sei, ed e' la prima cosa da leggere quando la tabella sotto e' vuota: se non e' mai stato scaricato niente per questo paese, se la preparazione e' in corso e da che ora, se l'ultimo tentativo e' fallito e per quale motivo, oppure quando risale l'ultimo aggiornamento e quante pubblicazioni restano senza prodotto. Segnala anche quante inserzioni ${platformLabel} espone senza SellerSku: quelle non sono agganciabili da qui.`,
			key: 'status',
			placement: 'bottom',
			target: '[data-tour="status"]',
			title: 'A che punto e la ricerca',
		},
		{
			body: `Cerca per SellerSku, codice o titolo, e filtra l'elenco gia' scaricato. Serve quando le pubblicazioni da agganciare sono tante e ne stai cercando una precisa, invece di scorrere pagina per pagina.`,
			key: 'search',
			placement: 'bottom',
			target: '[data-tour="search"]',
			title: 'Cercare una pubblicazione',
		},
		{
			body: `Attenzione a una cosa: immagine, titolo e prezzo di queste righe arrivano dal marketplace, non dal tuo catalogo - il prodotto qui non c'e' ancora, e' esattamente il motivo per cui la riga e' in questo elenco. Il Codice e' cliccabile e apre l'inserzione, cosi' puoi controllare di che si tratta prima di agganciarla. E se la valuta e' scritta in rosso vuol dire che non e' quella del paese selezionato: capita, e un prezzo in sterline in mezzo agli euro passerebbe inosservato.`,
			key: 'table',
			placement: 'top',
			target: '[data-tour="table"]',
			timeout: 250,
			title: 'Come si legge una riga',
		},
		{
			body: `Apre la maschera di abbinamento - la stessa che si apre dalle notifiche - dove scegli a quale prodotto del catalogo collegare la pubblicazione. Al salvataggio viene scritta solo la pubblicazione e non l'intero prodotto, e la riga sparisce da questo elenco: da quel momento prezzo e giacenza di quell'inserzione si gestiscono da Azseller come tutte le altre.`,
			key: 'associa',
			placement: 'left',
			target: '[data-tour="associa"]',
			timeout: 250,
			title: 'Associa',
		},
		{
			body: `Con le schede sotto il titolo passi alle altre pagine della sezione ${platformLabel}: Inserzioni (dove ritrovi, ora collegate, le pubblicazioni agganciate da qui), Analisi prezzi, Regole feed e Repricing.`,
			key: 'tabs',
			placement: 'bottom',
			target: '[data-tour="tabs"]',
			title: 'Le altre pagine',
		},
	];
}
